import React, { useState } from 'react';
import { ScrollView, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { GridEditor } from '@/components/schedule/grid-editor';
import { Button } from '@/components/ui/button';
import { PressableScale } from '@/components/ui/pressable-scale';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { usePatchDraft } from '@/hooks/use-onboarding-interview';
import { AvailabilityInterval } from '@/types/appointment';
import { WidgetCard } from './widget-card';
import { WeeklyGridWidgetData, WidgetProps } from './widget-types';

/**
 * A semana pintada — o que é mais difícil de conferir por texto.
 *
 * No passo da grade ela já vem preenchida com a semana comum do ramo: quem
 * abre no horário de sempre confirma com um toque, quem não abre toca em
 * "Ajustar" e usa o mesmo `GridEditor` da tela de agenda. Os dois caminhos
 * salvam por `PATCH /draft`, sem passar pelo modelo.
 */
export function WeeklyGridWidget({ data, editable, onAdvance }: WidgetProps<WeeklyGridWidgetData>) {
  const [open, setOpen] = useState(false);
  /** Confirmado uma vez, o cartão para de aceitar toque. */
  const [done, setDone] = useState(false);
  const patch = usePatchDraft();

  const intervals: AvailabilityInterval[] = (data.days ?? []).flatMap((day) =>
    day.intervals.map((interval) => ({
      weekday: day.weekday,
      startTime: interval.startTime,
      endTime: interval.endTime,
    })),
  );

  const [draftGrid, setDraftGrid] = useState<AvailabilityInterval[]>(intervals);

  const handleOpen = () => {
    setDraftGrid(intervals);
    setOpen(true);
  };

  const handleSave = async () => {
    await patch.mutateAsync({ payload: { availabilityGrid: draftGrid } });
    setOpen(false);
  };

  /**
   * "É isso mesmo": a semana na tela ainda é a sugestão do ramo, e confirmar
   * precisa gravá-la antes de a conversa andar — senão o passo continua em
   * aberto e o fecho da entrevista assume o mesmo valor sem ele ter visto.
   */
  const handleConfirm = async () => {
    if (done) return;
    setDone(true);
    if (data.answered === false) {
      await patch.mutateAsync({ payload: { availabilityGrid: intervals } });
    }
    onAdvance?.((data.lines ?? []).join(', ') || 'Confirmei meus horários.');
  };

  return (
    <>
      <WidgetCard
        title="Sua semana"
        icon="calendar-outline"
        action={
          editable ? (
            <PressableScale onPress={handleOpen} className="rounded-full px-2 py-1">
              <ThemedText className="font-semibold text-xs text-primary">Ajustar</ThemedText>
            </PressableScale>
          ) : undefined
        }
      >
        <View className="gap-1.5">
          {(data.lines ?? []).map((line, index) => (
            <ThemedText key={index} className="text-sm text-foreground/70">
              {line}
            </ThemedText>
          ))}
        </View>

        {editable && data.prompt && onAdvance && !done ? (
          <PressableScale
            onPress={() => void handleConfirm()}
            disabled={patch.isPending}
            className="items-center rounded-full bg-primary py-2.5"
          >
            <ThemedText className="font-semibold text-sm text-white">
              {patch.isPending ? 'Salvando...' : (data.confirmLabel ?? 'Pronto')}
            </ThemedText>
          </PressableScale>
        ) : null}
      </WidgetCard>

      <Sheet open={open} onOpenChange={setOpen} side="bottom">
        <SheetContent className="flex-1 gap-4">
          <ThemedText className="font-bold text-lg text-foreground">
            Ajustar seus horários
          </ThemedText>
          <ScrollView
            keyboardShouldPersistTaps="handled"
            className="flex-1"
            contentContainerClassName="pb-6"
            showsVerticalScrollIndicator={false}
          >
            <GridEditor value={draftGrid} onChange={setDraftGrid} />
          </ScrollView>
          <Button onPress={() => void handleSave()} disabled={patch.isPending}>
            <ThemedText className="font-semibold text-white">
              {patch.isPending ? 'Salvando...' : 'Salvar'}
            </ThemedText>
          </Button>
        </SheetContent>
      </Sheet>
    </>
  );
}
